import { DivTooltip } from '@wg/wows-react-uikit';
import classnames from 'classnames';
import equal from 'fast-deep-equal/react';
import {
    memo, useCallback, useMemo, useRef
} from 'react';
import styles from './ProgressBarItem.scss';
import { useAppDispatch } from '~/hooks';
import { openWelcomeDialog } from '~/store/dialogs';
import Reward from '~/Components/Reward/Reward';
import RewardHighlight from '~/Components/RewardHighlight/RewardHighlight';
import RewardShip from '~/Components/RewardShip/RewardShip';
import RewardShipTooltip from '~/Components/Tooltips/RewardShipTooltip/RewardShipTooltip';
import RewardTooltip from '~/Components/Tooltips/RewardTooltip/RewardTooltip';
import Wreath from '~/Components/Wreath/Wreath';
import * as Api from '~/types';
import { getShipIdFromReward, getWreathColor } from '~/utils/rewards';
import { REWARD_COLORS } from '~/constants';

export interface ProgressBarItem_Props {
    reward: Api.IReward,
    issuedReward?: Api.IReward,
    shipLevel: Api.ShipLevel,
    isPremiumLevel: boolean,
    isReached: boolean,
    isActive: boolean,
    isFreeMainReward: boolean,
    index: number,
    isBlueprint: boolean,
    keySuffix?: string,
    secondaryShip?: Api.GameItemReward | null,
}

const ProgressBarItem: React.FC<ProgressBarItem_Props> = ({
    isPremiumLevel,
    isReached,
    isActive,
    isFreeMainReward,
    issuedReward,
    shipLevel,
    reward,
    index,
    isBlueprint,
    keySuffix,
    secondaryShip,
}) => {
    const shipId = getShipIdFromReward(reward);
    const dispatch = useAppDispatch();
    const wrapper = useRef<HTMLDivElement | null>(null);
    const isCollected = !!issuedReward;
    const hasWreath = reward.grade !== 'normal';
    const wreathColor = getWreathColor(reward.grade);

    const color = isBlueprint ? REWARD_COLORS.BLUE : REWARD_COLORS.YELLOW;

    const collectedMark = classnames(styles.collectedMark, {
        [styles['collectedMark-yellow']]: true,
    });

    const rewardImage = classnames(styles.rewardImage, {
        [styles.rewardImageCollected]: isCollected,
    });

    const levelLabel = classnames(styles.levelLabel, {
        [styles.levelLabelReached]: isReached,
    });

    const shipRewardWrapper = classnames(styles.shipRewardTopMargin);

    const getTooltip = useMemo(() => {
        return shipId ? (
            <RewardShipTooltip
                id={shipId}
                reward={reward}
                isCollected={isCollected}
                isPremium={isPremiumLevel}
                isFreeMainReward={isFreeMainReward}
                secondaryShip={secondaryShip}
            />
        ) : (
            <RewardTooltip
                shipLevelDescription={shipLevel.description || undefined}
                reward={reward}
                isCollected={isCollected}
                isPremium={isPremiumLevel}
                isFreeMainReward={isFreeMainReward}
                secondaryShip={secondaryShip}
            />
        );
    }, [isCollected, isPremiumLevel, reward, shipId, shipLevel.description, secondaryShip, isFreeMainReward]);

    const renderReward = useMemo(() => {
        return shipId ? (
            <div className={shipRewardWrapper}>
                <RewardShip
                    id={shipId}
                    isCollected={isCollected}
                    isPremium={isPremiumLevel}
                    fixedTooltip={true}
                    raysVisible={isActive}
                    reward={reward}
                    clickableVehicles={true}
                />
            </div>
        ) : (
            <Reward
                reward={reward}
                shipLevelDescription={shipLevel.description || undefined}
                isPremium={isPremiumLevel}
                isCollected={isCollected}
                fixedTooltip
                isSmall
                noCount
                useIcon
            />
        );
    }, [isActive, isCollected, isPremiumLevel, reward, shipId, shipLevel.description, shipRewardWrapper]);

    const onClick = useCallback(() => {
        if (isReached && !isCollected) {
            dispatch(openWelcomeDialog(true));
        }
    }, [dispatch, isCollected, isReached]);

    return (
        <div ref={wrapper} className={styles.wrapper} onClick={onClick}>
            <div className={styles.highlight}>
                <RewardHighlight isVisible={!shipId && isActive} color={color} />
            </div>
            {getTooltip && (
                <DivTooltip
                    className={styles.inner}
                    tooltipBody={getTooltip}
                    fixed
                    position={'top'}
                    delayPositioning={0}
                    key={keySuffix}
                >
                    <div className={styles.reward}>
                        <div
                            id={shipId ? `progresbar-reward-image-ship-${index}` : `progresbar-reward-image-${index}`}
                            className={rewardImage}
                        >
                            {renderReward}
                        </div>
                        {isCollected && <div className={collectedMark} />}
                    </div>
                    <div className={levelLabel}>
                        {hasWreath && (
                            <div className={styles.wreath}>
                                <Wreath color={wreathColor} />
                            </div>
                        )}
                        {reward.shipLevelNumber}
                    </div>
                </DivTooltip>
            )}
        </div>
    );
};

export default memo(ProgressBarItem, equal);
